import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Select, MenuItem, FormControl,
  InputLabel, Stack, Card, CardContent, useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminReports } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import useListQueryState from "../../../hooks/useListQueryState";

const statusColor = { PENDING: "warning", REVIEWED: "info", DISMISSED: "default", ACTIONED: "success" };

const ReportList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get("page")) || 0;
  const statusFilter = searchParams.get("status") || "";
  const entityTypeFilter = searchParams.get("entityType") || "";
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reports", page, statusFilter, entityTypeFilter],
    queryFn: () => getAdminReports({ page: page + 1, limit, status: statusFilter || undefined, entityType: entityTypeFilter || undefined }),
  });

  const handleRowClick = (reportId) =>
    navigate(generatePath(frontendRoutes.ADMIN_REPORT_DETAIL, { reportId }));

  if (isLoading) return <CircularProgress />;

  const reports = data?.reports || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Reports</Typography>
      <Stack direction="row" spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => updateParams({ status: e.target.value, page: undefined })}>
            <MenuItem value="">All</MenuItem>
            {["PENDING", "REVIEWED", "DISMISSED", "ACTIONED"].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select value={entityTypeFilter} label="Entity Type" onChange={(e) => updateParams({ entityType: e.target.value, page: undefined })}>
            <MenuItem value="">All</MenuItem>
            {["PRODUCT", "SHOP", "USER"].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isMobile ? (
        <Stack spacing={2}>
          {reports.map((r) => (
            <Card key={r._id} variant="outlined" onClick={() => handleRowClick(r._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{r.entityType}</Typography>
                <Typography variant="body2">{r.reason?.replace(/_/g, " ")}</Typography>
                <Typography variant="body2" color="text.secondary">{new Date(r.createdAt).toLocaleDateString()}</Typography>
                <Chip label={r.status} color={statusColor[r.status]} size="small" sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entity Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Reporter</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r._id} hover onClick={() => handleRowClick(r._id)} sx={{ cursor: "pointer" }}>
                <TableCell><Chip label={r.entityType} size="small" /></TableCell>
                <TableCell>{r.reason?.replace(/_/g, " ")}</TableCell>
                <TableCell>{r.reportedBy?.name || r.reportedBy?.toString?.() || "—"}</TableCell>
                <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                <TableCell><Chip label={r.status} color={statusColor[r.status]} size="small" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default ReportList;
